import type { EquityDataRequestForAPIClient } from "@/interfaces/dataRequest/equity";
import type {
  MetricsAnalyticsRequest,
  QuestionsAnalyticsRequest,
  DistributionsAnalyticsRequest,
} from "@/interfaces/v2Analytics";
import {
  getRequestObjectForAPI,
  getRequestObjectForAPIForPrePost,
} from "@/utils/dataRequest/equity";
import {
  getRequestObjectForReputationAPI,
  getRequestObjectForReputationAPIForPrePost,
} from "@/utils/dataRequest/reputation";
import {
  getRequestObjectForVisibilityAPI,
  getRequestObjectForVisibilityAPIForPrePost,
} from "@/utils/dataRequest/visibility";
import { getPrevPeriodDateRange } from "@/utils/date";
import { apiInstances } from "@endpoints/config";
import {
  ADVERTISING_START_DATE,
  REPUTATION_START_DATE,
} from "@/constant/constantsThrougoutProject";
import { getAbortSignalForKey, clearAbortController } from "./requestManager";

export type QuestionsApiError = {
  error: string;
  /** HTTP status, so callers can distinguish "no data" from "bad request". */
  errorCode: number;
  /** The API's machine-readable code, e.g. VALIDATION_ERROR, BRANDSET_NOT_FOUND. */
  apiErrorCode?: string;
  message?: string;
};

/**
 * `/v2/analytics/questions` documents a whole error taxonomy — 400
 * (VALIDATION_ERROR, INVALID_REQUEST, MULTI_MARKET_REQUIRES_MARKET_GROUPING,
 * YOY_INTERVAL_NOT_SUPPORTED, RESULT_TOO_LARGE), 403, 404, 422 and 501 — that
 * collapsed to a blank "something went wrong" because the status and body were
 * never read.
 *
 * One body is off-envelope: `REFERENCE_NOT_RESOLVABLE` "does NOT follow
 * AnalyticsErrorResponse - it carries `code` (not `errorCode`) and omits
 * `status`, `error`, `message`, `path` and `timestamp`", so it has to be read
 * separately or it degrades to a 500.
 */
async function parseQuestionsError(err: unknown): Promise<QuestionsApiError> {
  const response = (err as { response?: Response })?.response;
  if (!response) return { error: "Something went wrong", errorCode: 500 };

  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // Non-JSON body — the status is still meaningful.
  }

  const apiErrorCode = (body.errorCode ?? body.code) as string | undefined;
  return {
    error: "Something went wrong",
    errorCode: response.status || 500,
    apiErrorCode,
    message: body.message as string | undefined,
  };
}

const dataService = {
  analyticsUnified: async (requestData: object, signal?: AbortSignal) => {
    const url = "v2/analytics/unified";
    // If an external signal is provided (e.g. from useModeViewData's AbortController),
    // use it directly so the caller controls cancellation. Otherwise fall back to
    // the URL-keyed abort mechanism that cancels duplicate in-flight requests.
    const abortSignal = signal ?? getAbortSignalForKey(url);
    try {
      const response = await apiInstances.data
        .post(url, {
          json: requestData,
          signal: abortSignal,
        })
        .json();
      return { response };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      if (!signal) clearAbortController(url);
    }
  },
  metricsCatalog: async (marketIds: number[]) => {
    const params = new URLSearchParams();
    marketIds.forEach((id) => params.append("marketIds", String(id)));
    const url = `v2/metrics/catalog?${params.toString()}`;
    try {
      const response = await apiInstances.data.get(url).json();
      return { response };
    } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  },
  analyticsV2: {
    // Generic endpoint — required whenever the request carries an explicit
    // `metrics[]` selection (e.g. TREND with a picked scoreType). The
    // per-module shortcut endpoints override metric selection server-side
    // and would expand to the whole module regardless of the body.
    metrics: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    equity: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/equity";
      // If an external signal is provided (e.g. from useModeViewData's AbortController),
      // use it directly so the caller controls cancellation. Otherwise fall back to
      // the URL-keyed abort mechanism that cancels duplicate in-flight requests.
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    conversion: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/conversion";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    imagery: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/imagery";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    advertising: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/advertising";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    visibility: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/visibility";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    aivisibility: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/ai-visibility";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    reputation: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/metrics/reputation";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    distributions: async (
      requestData: DistributionsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/distributions";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    questions: async (
      requestData: QuestionsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/analytics/questions";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return await parseQuestionsError(err);
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    butterfly: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/butterfly";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    differentiation: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/da";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
    perceptual: async (
      requestData: MetricsAnalyticsRequest,
      signal?: AbortSignal,
    ) => {
      const url = "v2/ca";
      const abortSignal = signal ?? getAbortSignalForKey(url);
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestData,
            signal: abortSignal,
          })
          .json();
        return { response };
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        if (!signal) clearAbortController(url);
      }
    },
  },
  equity: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/equity";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "equity";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  conversion: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/conversion";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "conversion";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  emotional: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/emotional";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "emotional";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  advertising: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "trend/advertising";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "advertising";
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
              restrictedDate: ADVERTISING_START_DATE,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        const url = "prepost";
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post(url, {
              json: requestPayload,
              signal: getAbortSignalForKey(url),
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          if ((err as any).name === "AbortError") {
            return "";
          }
          return { error: "Something went wrong", errorCode: 500 };
        } finally {
          clearAbortController(url);
        }
      },
    },
  },
  visibility: {
    getSegmentData: async (requestPayload: any) => {
      const url = `visibility/segment`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getBrandData: async (requestPayload: any) => {
      const url = `visibility/brand`;
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForVisibilityAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: ADVERTISING_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getMarketData: async (requestPayload: any) => {
      const url = `visibility/market`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      const url = `export?fileType=xlsx`;
      try {
        const response = await apiInstances.export
          .post(url, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getpptExport: async (requestPayload: any) => {
      const url = `dataexport?fileType=pptx`;
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  reputation: {
    getSegmentData: async (requestPayload: any) => {
      const url = `rq/segment`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getBrandData: async (requestPayload: any) => {
      const url = `rq/brand`;
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForReputationAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: REPUTATION_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getMarketData: async (requestPayload: any) => {
      const url = `rq/market`;
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        if ((error as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      const url = `export?fileType=xlsx`;
      try {
        const response = await apiInstances.export
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
    getpptExport: async (requestPayload: any) => {
      const url = `dataexport?fileType=pptx`;
      try {
        const response = await apiInstances.data
          .post(url, {
            json: requestPayload,
            signal: getAbortSignalForKey(url),
          })
          .text();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  questions: {
    createRollup: async (payload: any) => {
      try {
        const response = await apiInstances.data
          .post(`questions/rollups/save`, {
            json: payload,
          })
          .json();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    updateQuestionOptionColor: async (
      questionGroup: string,
      optionId: number,
      color: string,
      entityType: "RESPONSE" | "USER_ROLLUP" = "RESPONSE",
    ) => {
      try {
        const response = await apiInstances.data
          .put(`questions/customize/${questionGroup}`, {
            json: {
              color,
              entityId: optionId,
              entityType,
              sequence: 0,
            },
          })
          .text();
        return response === "Customization updated successfully."
          ? true
          : false;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  export: {
    exclusiveQuestionSegmentPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("segment/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionSegmentxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("segment/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("brand/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("brand/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getxlsxExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getpptExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  butterfly: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "butterfly";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      return { response, metaData: requestPayload };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
  differentiation: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "da";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return { response, metaData: requestPayload };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
  perceptual: async (requestData: EquityDataRequestForAPIClient) => {
    const url = "ca";
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post(url, {
          json: requestPayload,
          signal: getAbortSignalForKey(url),
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return {
        response: response,
        metaData: requestPayload,
      };
    } catch (err: unknown) {
      if ((err as any).name === "AbortError") {
        return "";
      }
      return { error: "Something went wrong", errorCode: 500 };
    } finally {
      clearAbortController(url);
    }
  },
};

export default dataService;
